import { readonly, ref } from 'vue';
import { fetchExactAccountUsers } from '@/api/contextAccountUsers.ts';
import User from '@/entities/User.js';

export type ExactAccountUsersState =
    | { readonly status: 'idle' }
    | { readonly status: 'loading'; readonly accountId: number }
    | {
          readonly status: 'success';
          readonly accountId: number;
          readonly users: readonly User[];
      }
    | {
          readonly status: 'error';
          readonly accountId: number;
          readonly error: unknown;
      };

export const useExactAccountUsersQuery = () => {
    const state = ref<ExactAccountUsersState>({ status: 'idle' });
    let generation = 0;

    const load = async (accountId: number): Promise<void> => {
        const currentGeneration = ++generation;
        state.value = { status: 'loading', accountId };

        try {
            const users = await fetchExactAccountUsers(accountId);

            if (currentGeneration !== generation) {
                return;
            }

            state.value = {
                status: 'success',
                accountId,
                users: users.map((user) => new User(user)),
            };
        } catch (error: unknown) {
            if (currentGeneration !== generation) {
                return;
            }

            state.value = { status: 'error', accountId, error };
        }
    };

    const reset = (): void => {
        generation += 1;
        state.value = { status: 'idle' };
    };

    return {
        state: readonly(state),
        load,
        reset,
    };
};
